import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "@clerk/react";
import { Info, Lock, Pencil, Plus, X } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ndd/ui";
import { authenticatedApi, ApiRequestError } from "@/lib/api";
import { can } from "@/lib/permissions";
import type { AlwaysAiRole } from "../../../../shared/api-types";
import {
  defaultMeetingNoteBodyJson,
  hasMeetingNoteBodyContent,
  meetingNotePlainText,
  todayIsoDate,
  type MeetingNoteBodyJson,
  type MeetingNoteData,
} from "./meeting-note-types";
import { DiscardChangesDialog } from "./discard-changes-dialog";
import { RichTextEditor } from "./rich-text-editor";
import { useFormDirtyGuard } from "./use-form-dirty-guard";

export interface SupervisorOption {
  id: string;
  name: string;
  email?: string;
}

interface MeetingNoteFormProps {
  rdId: string;
  note?: MeetingNoteData;
  viewerRole: AlwaysAiRole;
  currentUserId: string;
  currentUserName: string;
  rdSupervisorUserId: string | null;
  rdSupervisorName: string | null;
  supervisorOptions: SupervisorOption[];
  onSuccess: () => void;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const FIELD_BASE =
  "h-9 rounded-[10px] bg-background border-border text-foreground text-body md:text-body px-3 placeholder:text-subtle shadow-none";

const LABEL_BASE =
  "text-caption font-semibold uppercase tracking-wider text-muted-foreground";

function defaultSupervisorId({
  note,
  viewerRole,
  currentUserId,
  rdSupervisorUserId,
  supervisorOptions,
}: Pick<
  MeetingNoteFormProps,
  "note" | "viewerRole" | "currentUserId" | "rdSupervisorUserId" | "supervisorOptions"
>): string {
  if (note) return note.supervisorUserId;

  if (can(viewerRole, "supervisor_attribution:assign")) {
    const firstOption = supervisorOptions[0]?.id ?? currentUserId;

    return rdSupervisorUserId ?? firstOption;
  }

  return rdSupervisorUserId ?? currentUserId;
}

function readOnlySupervisorName({
  note,
  currentUserName,
  rdSupervisorName,
}: Pick<
  MeetingNoteFormProps,
  "note" | "currentUserName" | "rdSupervisorName"
>): string {
  return note?.supervisorName || rdSupervisorName || currentUserName;
}

interface MeetingNoteFields {
  title: string;
  meetingDate: string;
  fathomUrl: string;
  supervisorUserId: string;
  bodyJson: MeetingNoteBodyJson;
}

function initialFields(
  props: Pick<
    MeetingNoteFormProps,
    "note" | "viewerRole" | "currentUserId" | "rdSupervisorUserId" | "supervisorOptions"
  >
): MeetingNoteFields {
  const { note } = props;

  return {
    title: note?.title ?? "",
    meetingDate: note?.meetingDate ?? todayIsoDate(),
    fathomUrl: note?.fathomUrl ?? "",
    supervisorUserId: defaultSupervisorId(props),
    bodyJson: note?.bodyJson ?? defaultMeetingNoteBodyJson(),
  };
}

function canSaveFields(fields: MeetingNoteFields, bodyText: string): boolean {
  if (!fields.meetingDate || !fields.supervisorUserId) return false;

  return !!bodyText.trim() && hasMeetingNoteBodyContent(fields.bodyJson);
}

function submitErrorMessage(submitError: unknown): string {
  if (!(submitError instanceof ApiRequestError)) {
    return "Something went wrong. Please try again.";
  }

  const body = submitError.body as { error?: string } | undefined;

  return body?.error ?? "Failed to save meeting note.";
}

function submitLabel(isSubmitting: boolean, isEdit: boolean): string {
  if (isSubmitting) return "Saving...";

  return isEdit ? "Save changes" : "Save note";
}

/** The form's fields, and whether they differ from what was last saved. */
function useMeetingNoteFields(initial: MeetingNoteFields) {
  const [fields, setFields] = useState(initial);
  const [bodyText, setBodyText] = useState(meetingNotePlainText(initial.bodyJson));
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(initial));
  const currentSnapshot = JSON.stringify(fields);

  const setField = <K extends keyof MeetingNoteFields>(
    key: K,
    value: MeetingNoteFields[K]
  ): void => setFields((previous) => ({ ...previous, [key]: value }));

  return {
    fields,
    setField,
    setBodyText,
    canSave: canSaveFields(fields, bodyText),
    isDirty: currentSnapshot !== savedSnapshot,
    markSaved: () => setSavedSnapshot(currentSnapshot),
  };
}

function useMeetingNoteSubmit(
  rdId: string,
  note: MeetingNoteData | undefined,
  onSaved: () => void
) {
  const { getToken } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(fields: MeetingNoteFields): Promise<void> {
    setIsSubmitting(true);
    setError(null);

    try {
      const endpoint = note
        ? `/api/rds/${rdId}/meeting-notes/${note.id}`
        : `/api/rds/${rdId}/meeting-notes`;
      await authenticatedApi(getToken, endpoint, {
        method: note ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: fields.title.trim() || null,
          meetingDate: fields.meetingDate,
          supervisorUserId: fields.supervisorUserId,
          fathomUrl: fields.fathomUrl.trim() || null,
          bodyJson: fields.bodyJson,
        }),
      });

      onSaved();
    } catch (submitError) {
      setError(submitErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return { submit, isSubmitting, error };
}

export function MeetingNoteForm(props: MeetingNoteFormProps) {
  const { rdId, note, onSuccess, onClose, onDirtyChange } = props;
  const isEdit = note !== undefined;
  const [initial] = useState(() => initialFields(props));
  const form = useMeetingNoteFields(initial);
  const { submit, isSubmitting, error } = useMeetingNoteSubmit(rdId, note, () => {
    form.markSaved();
    onSuccess();
  });
  const dirtyGuard = useFormDirtyGuard(form.isDirty);

  useEffect(() => {
    onDirtyChange?.(form.isDirty);
  }, [form.isDirty, onDirtyChange]);

  const requestClose = (): void => dirtyGuard.requestClose(onClose);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.canSave || isSubmitting) return;

    void submit(form.fields);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-[14px] rounded-2xl border border-border bg-card px-5 py-[18px] shadow-[0_10px_24px_rgba(40,3,15,0.08)]"
    >
      <MeetingNoteFormHeader isEdit={isEdit} onClose={requestClose} />
      <MeetingNoteFormFields
        props={props}
        form={form}
        initialBody={initial.bodyJson}
        isSubmitting={isSubmitting}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <MeetingNoteFormFooter
        isDirty={form.isDirty}
        canSave={form.canSave}
        isSubmitting={isSubmitting}
        isEdit={isEdit}
        onCancel={requestClose}
      />
      <DiscardChangesDialog
        open={dirtyGuard.showDiscardDialog}
        onOpenChange={dirtyGuard.handleDiscardDialogOpenChange}
        onDiscard={(event) => {
          event.preventDefault();
          dirtyGuard.confirmDiscard(onClose);
        }}
        disabled={isSubmitting}
        description="This meeting note has edits that haven't been saved. If you leave now, those changes will be lost."
      />
    </form>
  );
}

function MeetingNoteFormHeader({
  isEdit,
  onClose,
}: {
  isEdit: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex min-w-0 items-center gap-[10px]">
        <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-primary-soft">
          {isEdit ? (
            <Pencil className="h-[13px] w-[13px] text-primary" strokeWidth={2.5} />
          ) : (
            <Plus className="h-[14px] w-[14px] text-primary" strokeWidth={2.5} />
          )}
        </div>
        <h3 className="shrink-0 font-sans text-title-sm font-semibold leading-[18px] text-foreground">
          {isEdit ? "Edit meeting note" : "Add meeting note"}
        </h3>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:text-foreground"
      >
        <X className="h-[14px] w-[14px]" />
      </button>
    </div>
  );
}

type MeetingNoteFieldsState = ReturnType<typeof useMeetingNoteFields>;

function MeetingNoteFormFields({
  props,
  form,
  initialBody,
  isSubmitting,
}: {
  props: MeetingNoteFormProps;
  form: MeetingNoteFieldsState;
  initialBody: MeetingNoteBodyJson;
  isSubmitting: boolean;
}) {
  const { fields, setField } = form;

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <MeetingNoteInput
          id="meeting-note-form-date"
          label="Meeting date"
          type="date"
          value={fields.meetingDate}
          onChange={(value) => setField("meetingDate", value)}
          disabled={isSubmitting}
        />
        <MeetingNoteSupervisorField
          props={props}
          value={fields.supervisorUserId}
          onChange={(value) => setField("supervisorUserId", value)}
          disabled={isSubmitting}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
        <MeetingNoteInput
          id="meeting-note-form-title"
          label="Title"
          optional
          value={fields.title}
          onChange={(value) => setField("title", value)}
          placeholder="e.g. Mid-cycle check-in"
          disabled={isSubmitting}
        />
        <MeetingNoteInput
          id="meeting-note-form-fathom"
          label="Fathom Link"
          optional
          value={fields.fathomUrl}
          onChange={(value) => setField("fathomUrl", value)}
          placeholder="https://fathom.video/..."
          disabled={isSubmitting}
        />
      </div>

      <MeetingNoteBodyField
        initialBody={initialBody}
        form={form}
        disabled={isSubmitting}
      />
    </>
  );
}

function MeetingNoteBodyField({
  initialBody,
  form,
  disabled,
}: {
  initialBody: MeetingNoteBodyJson;
  form: MeetingNoteFieldsState;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label className={LABEL_BASE}>Note</Label>
      <RichTextEditor
        initialValue={initialBody}
        disabled={disabled}
        onChange={(nextBodyJson, nextBodyText) => {
          form.setField("bodyJson", nextBodyJson);
          form.setBodyText(nextBodyText);
        }}
      />
    </div>
  );
}

function MeetingNoteInput({
  id,
  label,
  optional = false,
  type,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  id: string;
  label: string;
  optional?: boolean;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor={id} className={LABEL_BASE}>
        {label}
        {optional && (
          <>
            {" "}
            <span className="normal-case text-subtle">(optional)</span>
          </>
        )}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={FIELD_BASE}
      />
    </div>
  );
}

function MeetingNoteSupervisorField({
  props,
  value,
  onChange,
  disabled,
}: {
  props: MeetingNoteFormProps;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const canAssign = can(props.viewerRole, "supervisor_attribution:assign");

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Label htmlFor="meeting-note-form-supervisor" className={LABEL_BASE}>
        Supervisor{" "}
        {!canAssign && <span className="normal-case text-subtle">(read-only)</span>}
      </Label>
      {canAssign ? (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id="meeting-note-form-supervisor" className={FIELD_BASE}>
            <SelectValue placeholder="Select supervisor" />
          </SelectTrigger>
          <SelectContent>
            {props.supervisorOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="flex h-9 items-center justify-between rounded-[10px] border border-border bg-background px-3 text-body text-foreground">
          <span>{readOnlySupervisorName(props)}</span>
          <Lock className="h-3.5 w-3.5 text-subtle" />
        </div>
      )}
    </div>
  );
}

function MeetingNoteFormFooter({
  isDirty,
  canSave,
  isSubmitting,
  isEdit,
  onCancel,
}: {
  isDirty: boolean;
  canSave: boolean;
  isSubmitting: boolean;
  isEdit: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="inline-flex items-center gap-1 text-xs text-subtle">
        <Info className="h-3.5 w-3.5" />
        {isDirty ? "Unsaved changes" : "Nothing is saved until you choose Save."}
      </span>
      <div className="flex items-center justify-end gap-[10px]">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-9 rounded-[10px] border-border px-4 text-body font-medium"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!canSave || isSubmitting}
          className="h-9 rounded-[10px] bg-primary px-[18px] text-body font-medium text-primary-foreground shadow-sm hover:bg-primary-hover"
        >
          {submitLabel(isSubmitting, isEdit)}
        </Button>
      </div>
    </div>
  );
}
